import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import type { EventMessage } from '@azure/msal-browser';
import { EventType } from '@azure/msal-browser';

@Component({
  selector: 'app-userprofile',
  imports: [CommonModule, MatButtonModule, MatMenuModule, MatIconModule, MatDividerModule],
  templateUrl: './userprofile.html',
  styleUrl: './userprofile.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserProfileComponent {
  private readonly authService = inject(MsalService);
  private readonly msalEvents = inject(MsalBroadcastService);
  private readonly router = inject(Router);
  
  protected readonly isAuthenticated = signal(false);
  protected readonly username = signal<string>('');
  protected readonly displayName = signal<string>('');

  constructor() {
    // Check initial authentication state
    this.updateAuthState();
    
    // Subscribe to MSAL events to track auth state changes
    this.msalEvents.msalSubject$.subscribe((ev: EventMessage) => {
      if (ev.eventType === EventType.LOGIN_SUCCESS || 
          ev.eventType === EventType.LOGOUT_SUCCESS ||
          ev.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        this.updateAuthState();
      }
    });
  }

  private updateAuthState(): void {
    const accounts = this.authService.instance.getAllAccounts();
    const isAuth = accounts.length > 0;
    this.isAuthenticated.set(isAuth);
    
    if (isAuth && accounts[0]) {
      const account = accounts[0];
      this.username.set(account.username || '');
      this.displayName.set(account.name || account.username || '');
    } else {
      this.username.set('');
      this.displayName.set('');
    }
  }

  protected login(): void {
    this.router.navigate(['/login']);
  }

  protected logout(): void {
    this.authService.logout();
  }

  protected getInitials(): string {
    const name = this.displayName();
    if (!name) return '?';
    
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
}
